import { IonContent, IonPage, useIonAlert } from '@ionic/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { logoutApi } from '~/api/auth';
import { cleanupPushOnLogout } from '~/api/hooks/usePushSubscription';
import { initialsFrom } from '~/components/ui/Avatar';
import { Icon, type RxIconName } from '~/components/ui/Icon';
import { SectionHead } from '~/components/ui/SectionHead';
import { PLATFORM_NAME } from '~/config';
import { useAppNavigation } from '~/hooks/useAppNavigation';
import { countPendingInvoices } from '~/offline/draftDb';
import { NotificationsCard } from '~/pages/perfil/NotificationsCard';
import { ROUTES } from '~/routes';
import { clearLocalData } from '~/session/clearLocalData';
import { useAuthStore } from '~/stores/auth';
import type { components } from '~/types/api';

type UserType = components['schemas']['UserTypeEnum'];

const USER_TYPE_LABEL: Record<UserType, string> = {
  PLATFORM_ADMIN: 'Administrador da plataforma',
  PLATFORM_EMPLOYEE: 'Colaborador da plataforma',
  OFFICE_ADMIN: 'Administrador',
  OFFICE_EMPLOYEE: 'Colaborador',
  CLIENT: 'Cliente',
};

function pendingInvoicesWarning(count: number): string {
  const notas =
    count === 1 ? '1 nota ainda não foi enviada' : `${count} notas ainda não foram enviadas`;
  return `${notas}. Sair agora descarta essas emissões deste aparelho.`;
}

export function Perfil(): JSX.Element {
  const user = useAuthStore((state) => state.user);
  const companies = useAuthStore((state) => state.companies);
  const nav = useAppNavigation();
  const queryClient = useQueryClient();
  const [presentAlert] = useIonAlert();

  const logoutMutation = useMutation({
    mutationFn: async () => {
      // Limpa subscription Web Push antes de invalidar token —
      // a chamada DELETE precisa de Authorization válido.
      await cleanupPushOnLogout();
      // Lido agora, não no render: um 401 durante o DELETE acima rotaciona o refresh,
      // e o capturado antes já estaria na blacklist enquanto o novo ficaria vivo.
      const { refreshToken } = useAuthStore.getState();
      if (refreshToken) {
        try {
          await logoutApi(refreshToken);
        } catch {
          /* best-effort: blacklist falha não trava logout local */
        }
      }
    },
    onSettled: async () => {
      // Quem compartilha o aparelho não pode ver as notas de quem saiu.
      await clearLocalData(queryClient);
      nav.replace(ROUTES.login);
    },
  });

  const requestLogout = async (): Promise<void> => {
    const pending = await countPendingInvoices();
    if (pending === 0) {
      logoutMutation.mutate();
      return;
    }
    await presentAlert({
      header: 'Notas aguardando envio',
      message: pendingInvoicesWarning(pending),
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        { text: 'Sair mesmo assim', role: 'destructive', handler: () => logoutMutation.mutate() },
      ],
    });
  };

  const fullName = user?.full_name || user?.username || 'Cliente';
  const initials = initialsFrom(fullName);
  const userTypeLabel = user ? USER_TYPE_LABEL[user.user_type] : null;

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="rx-container rx-screen" style={{ paddingBottom: 28 }}>
          <div style={{ padding: '12px 24px 0' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <button
                type="button"
                className="rx-iconbtn"
                onClick={() => nav.back()}
                aria-label="Voltar"
              >
                <Icon name="back" size={18} />
              </button>
              <h1
                style={{
                  margin: 0,
                  fontSize: 22,
                  fontWeight: 500,
                  color: 'var(--rx-text)',
                  letterSpacing: '-0.4px',
                }}
              >
                Perfil
              </h1>
              <div style={{ width: 36 }} />
            </div>
          </div>

          {/* Identity card */}
          <div style={{ padding: '16px 24px 0' }}>
            <div
              style={{
                background: 'var(--rx-surface)',
                border: '0.5px solid var(--rx-border)',
                borderRadius: 18,
                padding: 20,
                display: 'flex',
                alignItems: 'center',
                gap: 14,
              }}
            >
              <div
                style={{
                  position: 'relative',
                  width: 64,
                  height: 64,
                  borderRadius: 16,
                  background: 'var(--rx-brand-tint)',
                  color: 'var(--rx-brand)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Geist, ui-sans-serif, sans-serif',
                  fontWeight: 600,
                  fontSize: 22,
                  letterSpacing: '-0.5px',
                }}
              >
                {initials}
                <div
                  style={{
                    position: 'absolute',
                    right: -3,
                    bottom: -3,
                    width: 22,
                    height: 22,
                    borderRadius: 22,
                    background: 'var(--rx-positive)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid var(--rx-surface)',
                  }}
                >
                  <Icon name="check" size={12} />
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 17,
                    fontWeight: 500,
                    color: 'var(--rx-text)',
                    letterSpacing: '-0.3px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fullName}
                </div>
                {user?.role && (
                  <div
                    style={{
                      marginTop: 2,
                      fontSize: 12,
                      color: 'var(--rx-text-2)',
                    }}
                  >
                    {user.role}
                    {userTypeLabel ? ` · ${userTypeLabel}` : ''}
                  </div>
                )}
                {user?.email && (
                  <div
                    className="rx-mono"
                    style={{
                      marginTop: 6,
                      fontSize: 11,
                      color: 'var(--rx-text-3)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {user.email}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pessoal */}
          <SectionHead label="Conta" />
          <div style={{ padding: '0 24px' }}>
            <Group
              rows={[
                userTypeLabel ? { icon: 'user', label: 'Tipo', meta: userTypeLabel } : null,
                user?.role ? { icon: 'shield', label: 'Cargo', meta: user.role } : null,
                user?.phone
                  ? { icon: 'help', label: 'Telefone', meta: user.phone, mono: true }
                  : null,
                user?.cpf ? { icon: 'lock', label: 'CPF', meta: user.cpf, mono: true } : null,
              ]}
            />
          </div>

          {/* Notificações Web Push */}
          <SectionHead label="Notificações" />
          <div style={{ padding: '0 24px' }}>
            <NotificationsCard />
          </div>

          {/* Empresas */}
          {companies.length > 0 && (
            <>
              <SectionHead label="Empresas vinculadas" />
              <div style={{ padding: '0 24px' }}>
                <Group
                  rows={companies.map((company) => ({
                    icon: 'ledger',
                    label: company.razao_social,
                    meta: company.cpf_cnpj_formatted,
                    mono: false,
                  }))}
                />
              </div>
            </>
          )}

          {/* Compliance footer */}
          <div style={{ padding: '20px 24px 0' }}>
            <div
              style={{
                padding: '10px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: 'var(--rx-surface-alt)',
                borderRadius: 12,
              }}
            >
              <Icon name="shield" size={16} color="var(--rx-text-3)" />
              <p
                style={{
                  flex: 1,
                  fontSize: 11,
                  color: 'var(--rx-text-2)',
                  lineHeight: 1.45,
                }}
              >
                Dados criptografados ponta-a-ponta. Conforme LGPD.
              </p>
            </div>
          </div>

          {/* Logout */}
          <div style={{ padding: '24px 24px 0' }}>
            <button
              type="button"
              onClick={() => void requestLogout()}
              disabled={logoutMutation.isPending}
              style={{
                width: '100%',
                padding: '14px 16px',
                background: 'var(--rx-surface)',
                border: '0.5px solid var(--rx-negative)',
                borderRadius: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                cursor: 'pointer',
                textAlign: 'left',
                opacity: logoutMutation.isPending ? 0.6 : 1,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 9,
                  background: 'var(--rx-negative-tint)',
                  color: 'var(--rx-negative)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon name="logout" size={18} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--rx-negative)' }}>
                  Sair da conta
                </div>
                <div style={{ marginTop: 2, fontSize: 12, color: 'var(--rx-text-2)' }}>
                  Você precisará entrar de novo.
                </div>
              </div>
              <Icon name="chevron" size={16} color="var(--rx-negative)" />
            </button>
          </div>

          <div style={{ padding: '20px 24px 0', textAlign: 'center' }}>
            <div
              className="rx-mono"
              style={{
                fontSize: 10,
                fontWeight: 500,
                letterSpacing: '0.2px',
                color: 'var(--rx-text-3)',
                opacity: 0.7,
              }}
            >
              {PLATFORM_NAME} · NFSe v0.1
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}

type GroupRow = {
  icon: RxIconName;
  label: string;
  meta?: string | null;
  mono?: boolean;
} | null;

function Group({ rows }: { rows: GroupRow[] }): JSX.Element {
  const filtered = rows.filter(
    (r): r is { icon: RxIconName; label: string; meta?: string | null; mono?: boolean } =>
      r !== null,
  );
  return (
    <div
      style={{
        background: 'var(--rx-surface)',
        border: '0.5px solid var(--rx-border)',
        borderRadius: 14,
        overflow: 'hidden',
      }}
    >
      {filtered.map((row, idx) => (
        <div
          key={`${row.label}-${idx}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '13px 14px',
            borderTop: idx === 0 ? 'none' : '0.5px solid var(--rx-border)',
          }}
        >
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              background: 'var(--rx-surface-alt)',
              color: 'var(--rx-text-2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon name={row.icon} size={16} />
          </div>
          <div
            style={{
              flex: 1,
              fontSize: 14,
              color: 'var(--rx-text)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.label}
          </div>
          {row.meta && (
            <span
              className={row.mono ? 'rx-mono' : ''}
              style={{
                fontSize: 11,
                color: 'var(--rx-text-3)',
                maxWidth: '50%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.meta}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
